import cn from "classnames";
import { type ReactNode } from "react";
import { ColumnAlignment } from "@/constants/content";
import { getColumnAlignmentClassName } from "@/utils/style/getColumnAlignmentClassName";
import type { BaseSiteBuilderModule } from "@/types/site-builder";
import s from "./ColumnGrid.module.css";

type Display<T> = BaseSiteBuilderModule<T>["style"]["display"];

export interface ColumnGridProps<T>
  extends Pick<Display<T>, "contentLayout">,
    Partial<Pick<Display<T>, "columnAlignment">> {
  items: T[];
  renderItem(block: T, index: number): ReactNode;
  customContentLayout?: string;
}

export default function ColumnGrid<T>(props: ColumnGridProps<T>) {
  const {
    items,
    renderItem,
    contentLayout,
    customContentLayout = "col-12 col-sm-6",
    columnAlignment = ColumnAlignment.Left,
  } = props;

  if (items.length === 0) return null;

  let colIndex = -1;
  const cols = contentLayout.split(":");
  const colsLength = cols.length;
  const lastItemIndex = items.length - 1;
  const stretchLastItem = columnAlignment === ColumnAlignment.Stretch;

  const className = cn(
    "row",
    s.rowGap,
    getColumnAlignmentClassName(columnAlignment),
  );

  return (
    <div className={className}>
      {items.map((item, index) => {
        colIndex++;
        if (colIndex >= colsLength) {
          colIndex = 0;
        }

        const columnClassName = cn(
          stretchLastItem && index === lastItemIndex
            ? "col"
            : `${customContentLayout} col-lg-${cols[colIndex]}`,
        );

        return (
          <div key={index} className={columnClassName}>
            {renderItem(item, index)}
          </div>
        );
      })}
    </div>
  );
}
